<template>
  <div class="message-board-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>留言板管理</span>
        </div>
      </template>

      <div class="search-container">
        <el-input
          v-model="searchKeyword"
          placeholder="搜索学生姓名"
          style="width: 300px;"
          suffix-icon="el-icon-search"
        />
        <el-button type="primary" @click="searchMessages">搜索</el-button>
        <el-button @click="resetSearch">重置</el-button>
      </div>

      <el-table :data="messagesData" style="width: 100%" border>
        <el-table-column prop="consultationId" label="咨询id" width="80"></el-table-column>
        <el-table-column prop="studentId" label="学生id" width="100"></el-table-column>
        <el-table-column prop="studentName" label="学生姓名" width="100"></el-table-column>
        <el-table-column prop="teacherId" label="教师id" width="100"></el-table-column>
        <el-table-column prop="teacherName" label="教师姓名" width="100"></el-table-column>
        <el-table-column prop="questionContent" label="问题内容" width="200">
          <template #default="scope">
            <div class="content-truncate" :title="scope.row.questionContent">
              {{ scope.row.questionContent }}
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="replyContent" label="回复内容" width="200">
          <template #default="scope">
            <div class="content-truncate" :title="scope.row.replyContent || '暂无回复'">
              {{ scope.row.replyContent || '暂无回复' }}
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="80">
          <template #default="scope">
            <el-tag v-if="scope.row.status === '已回复'" type="success">已回复</el-tag>
            <el-tag v-else type="warning">待回复</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="time" label="时间" width="150">
          <template #default="scope">
            {{ formatDate(scope.row.time) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template #default="scope">
            <el-button type="text" @click="editMessage(scope.row)" style="display: inline-block; margin-right: 10px; font-size: 14px;">编辑</el-button>
            <el-button type="text" @click="deleteMessage(scope.row.consultationId)" style="display: inline-block; font-size: 14px;">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          v-model:current-page="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 编辑留言对话框 -->
    <el-dialog v-model="editDialogVisible" title="编辑留言" width="50%">
      <el-form v-if="editMessageData" :model="editMessageData" label-width="120px">
        <el-form-item label="咨询id" disabled>
          <el-input v-model="editMessageData.consultationId" disabled></el-input>
        </el-form-item>
        <el-form-item label="学生id" disabled>
          <el-input v-model="editMessageData.studentId" disabled></el-input>
        </el-form-item>
        <el-form-item label="学生姓名" disabled>
          <el-input v-model="editMessageData.studentName" disabled></el-input>
        </el-form-item>
        <el-form-item label="教师id" disabled>
          <el-input v-model="editMessageData.teacherId" disabled></el-input>
        </el-form-item>
        <el-form-item label="教师姓名" disabled>
          <el-input v-model="editMessageData.teacherName" disabled></el-input>
        </el-form-item>
        <el-form-item label="问题内容">
          <el-input v-model="editMessageData.questionContent" type="textarea" rows="4"></el-input>
        </el-form-item>
        <el-form-item label="回复内容">
          <el-input v-model="editMessageData.replyContent" type="textarea" rows="4"></el-input>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="editMessageData.status">
            <el-option value="已回复">已回复</el-option>
            <el-option value="待回复">待回复</el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="editDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitEdit">保存</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import request from '@/utils/request';
import { ElMessage } from 'element-plus';

// 学生姓名搜索关键字
const searchKeyword = ref('');
// 留言数据
const messagesData = ref([]);
// 当前页码
const currentPage = ref(1);
// 每页条数
const pageSize = ref(10);
// 总条数
const total = ref(0);
// 编辑对话框可见性
const editDialogVisible = ref(false);
// 编辑留言数据
const editMessageData = ref(null);

// 挂载时获取留言列表
onMounted(() => {
  fetchMessages();
});

import axios from 'axios';

// 获取留言列表
const fetchMessages = async () => {
  try {
    // 使用axios直接请求，绕过request拦截器的code验证
    const res = await axios.get('/admin/message-board/list', {
      params: {
        page: currentPage.value,
        size: pageSize.value,
        studentName: searchKeyword.value
      }
    });
    console.log('后端返回的数据:', res.data);
    // 正确设置数据，确保表格能显示
    messagesData.value = res.data.consultations || [];
    total.value = res.data.total || 0;
  } catch (error) {
    console.error('获取留言列表失败', error);
    ElMessage.error('获取留言列表失败');
  }
};

// 搜索留言
const searchMessages = () => {
  currentPage.value = 1;
  fetchMessages();
};

// 重置搜索
const resetSearch = () => {
  searchKeyword.value = '';
  currentPage.value = 1;
  fetchMessages();
};

// 编辑留言
const editMessage = (message) => {
  // 深拷贝留言数据，避免直接修改原数据
  editMessageData.value = JSON.parse(JSON.stringify(message));
  editDialogVisible.value = true;
};

// 提交编辑
const submitEdit = async () => {
  try {
    const response = await request.post('/admin/message-board/update', {
      consultationId: editMessageData.value.consultationId,
      replyContent: editMessageData.value.replyContent,
      status: editMessageData.value.status
    });
    ElMessage.success(response.data.message || '编辑成功');
    editDialogVisible.value = false;
    fetchMessages();
  } catch (error) {
    console.error('编辑留言失败', error);
    ElMessage.error('编辑留言失败');
  }
};

// 删除留言
const deleteMessage = async (id) => {
  try {
    const response = await request.delete(`/admin/message-board/delete/${id}`);
    ElMessage.success(response.data.message || '删除成功');
    fetchMessages();
  } catch (error) {
    console.error('删除留言失败', error);
    ElMessage.error('删除留言失败');
  }
};

// 分页处理
const handleSizeChange = (size) => {
  pageSize.value = size;
  fetchMessages();
};

const handleCurrentChange = (current) => {
  currentPage.value = current;
  fetchMessages();
};

// 格式化日期函数，只显示年月日
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  
  // 创建日期对象
  const date = new Date(dateStr);
  
  // 检查日期是否有效
  if (isNaN(date.getTime())) return dateStr;
  
  // 格式化日期为 YYYY-MM-DD
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};
</script>

<style scoped>
.message-board-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-container {
    margin: 10px 0;
    display: flex;
    gap: 10px;
  }

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.content-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>